import { useState } from 'react';
import { resumeApi, historyApi } from '../services/api';
import { sampleJobDescriptions } from '../data/mockData';

export default function ResumeForm() {
    // Tab state
    const [activeTab, setActiveTab] = useState('profile'); // 'manual' or 'profile'

    // Manual mode state
    const [resumeText, setResumeText] = useState('');
    const [jobDescription, setJobDescription] = useState('');
    const [generatedResume, setGeneratedResume] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Profile mode state
    const [profileJobDescription, setProfileJobDescription] = useState('');
    const [selectionPreview, setSelectionPreview] = useState(null);
    const [tailoredResume, setTailoredResume] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState(null);
    const [showPreview, setShowPreview] = useState(false);

    // Template options
    const [template, setTemplate] = useState('professional');
    const [aiOptimize, setAiOptimize] = useState(true);

    // Preview tab state
    const [previewTab, setPreviewTab] = useState('markdown'); // 'markdown' or 'pdf'

    // Load sample job description
    const loadSampleJob = (index) => {
        const sample = sampleJobDescriptions[index];
        if (sample) {
            setProfileJobDescription(sample.description);
        }
    };

    // Manual mode: Generate resume
    const generateResume = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await resumeApi.generateResume({
                resumeText,
                jobDescription,
            });
            setGeneratedResume(response.markdown);
        } catch (err) {
            setError('Error generating resume. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    // Profile mode: Preview selection
    const handlePreviewSelection = async () => {
        setProfileLoading(true);
        setProfileError(null);
        setShowPreview(true);
        try {
            const response = await resumeApi.previewSelection(profileJobDescription);
            setSelectionPreview(response);
        } catch (err) {
            setProfileError(err.message || 'Error previewing selection. Please try again.');
            setShowPreview(false);
        } finally {
            setProfileLoading(false);
        }
    };

    // Profile mode: Generate tailored resume
    const handleGenerateTailored = async () => {
        setProfileLoading(true);
        setProfileError(null);
        try {
            const response = await resumeApi.generateTailored(profileJobDescription, {
                template,
                aiOptimize,
            });
            setTailoredResume(response);
            setShowPreview(false);
        } catch (err) {
            setProfileError(err.message || 'Error generating tailored resume. Please try again.');
        } finally {
            setProfileLoading(false);
        }
    };

    // Download DOCX handler for profile mode
    const handleDownloadDocx = async () => {
        try {
            const blob = await historyApi.downloadDocx(1);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `tailored_resume_${Date.now()}.docx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            setProfileError('Error downloading resume. Please try again.');
        }
    };

    // Download PDF handler (demo mode - downloads text version)
    const handleDownloadPdf = async () => {
        try {
            // In demo mode, we create a text blob
            const content = tailoredResume?.markdown || 'Demo Resume Content';
            const blob = new Blob([content], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `tailored_resume_${Date.now()}.txt`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            setProfileError('Error downloading PDF. Please try again.');
        }
    };

    return (
        <div className="resume-form">
            <div className="form-header">
                <h2 className="form-title">إنشاء سيرة ذاتية متوافقة مع أنظمة التوظيف</h2>
                <p className="form-description">
                    أنشئ سير ذاتية محسّنة من ملفك الشخصي أو الصق محتوى موجود
                </p>
                <div className="demo-badge">
                    <span className="demo-badge-text">وضع تجريبي - استجابات محاكاة</span>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="tab-navigation">
                <button
                    className={`tab-button ${activeTab === 'manual' ? 'active' : ''}`}
                    onClick={() => setActiveTab('manual')}
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M4 2h8a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2zm1 2v1h6V4H5zm0 3v1h6V7H5zm0 3v1h4v-1H5z"/>
                    </svg>
                    إدخال يدوي
                </button>
                <button
                    className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
                    onClick={() => setActiveTab('profile')}
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 8a3 3 0 100-6 3 3 0 000 6zm-6 8a6 6 0 1112 0H2z"/>
                    </svg>
                    من الملف الشخصي
                </button>
            </div>

            <div className="form-sections">
                {/* Manual Entry Tab */}
                {activeTab === 'manual' && (
                    <div className="input-section">
                        <div className="input-group">
                            <label htmlFor="resume-input" className="input-label">
                                <svg className="label-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M4 2h8a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2zm1 2v1h6V4H5zm0 3v1h6V7H5zm0 3v1h4v-1H5z"/>
                                </svg>
                                سيرتك الذاتية
                            </label>
                            <textarea
                                id="resume-input"
                                className="form-textarea"
                                value={resumeText}
                                onChange={(e) => setResumeText(e.target.value)}
                                placeholder="الصق نص سيرتك الذاتية الحالية هنا..."
                                rows="12"
                            />
                            <div className="char-count">{resumeText.length} حرف</div>
                        </div>

                        <div className="input-group">
                            <label htmlFor="job-input" className="input-label">
                                <svg className="label-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M6 2a2 2 0 00-2 2v1H2v9h12V5h-2V4a2 2 0 00-2-2H6zm0 1h4a1 1 0 011 1v1H5V4a1 1 0 011-1z"/>
                                </svg>
                                وصف الوظيفة
                            </label>
                            <textarea
                                id="job-input"
                                className="form-textarea"
                                value={jobDescription}
                                onChange={(e) => setJobDescription(e.target.value)}
                                placeholder="الصق وصف الوظيفة المستهدفة هنا..."
                                rows="8"
                            />
                            <div className="char-count">{jobDescription.length} حرف</div>
                        </div>

                        <button
                            className="generate-button"
                            onClick={generateResume}
                            disabled={loading || !resumeText || !jobDescription}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner"></span>
                                    <span>جاري إنشاء السيرة الذاتية...</span>
                                </>
                            ) : (
                                <>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                        <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm1 10.5L5.5 8l.707-.707L8 9.086l3.793-3.793.707.707L9 11.5z"/>
                                    </svg>
                                    <span>إنشاء السيرة الذاتية</span>
                                </>
                            )}
                        </button>

                        {error && (
                            <div className="error-message">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M8 1a7 7 0 100 14A7 7 0 008 1zM7 5h2v4H7V5zm0 5h2v2H7v-2z"/>
                                </svg>
                                <span>{error}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* From Profile Tab */}
                {activeTab === 'profile' && (
                    <div className="input-section">
                        {/* Sample Job Descriptions */}
                        <div className="sample-jobs">
                            <label className="input-label">
                                <svg className="label-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm-.5 3h1v5h-1V4zm0 6h1v1h-1v-1z"/>
                                </svg>
                                جرّب وصف وظيفة نموذجي
                            </label>
                            <div className="sample-buttons">
                                {sampleJobDescriptions.map((job, index) => (
                                    <button
                                        key={job.id}
                                        className="sample-button"
                                        onClick={() => loadSampleJob(index)}
                                    >
                                        {job.title} - {job.company}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="input-group">
                            <label htmlFor="profile-job-input" className="input-label">
                                <svg className="label-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M6 2a2 2 0 00-2 2v1H2v9h12V5h-2V4a2 2 0 00-2-2H6zm0 1h4a1 1 0 011 1v1H5V4a1 1 0 011-1z"/>
                                </svg>
                                وصف الوظيفة
                            </label>
                            <textarea
                                id="profile-job-input"
                                className="form-textarea"
                                value={profileJobDescription}
                                onChange={(e) => setProfileJobDescription(e.target.value)}
                                placeholder="الصق وصف الوظيفة المستهدفة هنا أو اختر نموذجاً من الأعلى..."
                                rows="8"
                            />
                            <div className="char-count">{profileJobDescription.length} حرف</div>
                        </div>

                        {/* Template and Options */}
                        <div className="options-group">
                            <div className="option-item">
                                <label htmlFor="template-select" className="option-label">
                                    نمط القالب
                                </label>
                                <select
                                    id="template-select"
                                    className="option-select"
                                    value={template}
                                    onChange={(e) => setTemplate(e.target.value)}
                                >
                                    <option value="professional">احترافي</option>
                                    <option value="modern">عصري</option>
                                    <option value="creative">إبداعي</option>
                                </select>
                            </div>

                            <div className="option-item">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={aiOptimize}
                                        onChange={(e) => setAiOptimize(e.target.checked)}
                                    />
                                    <span>تحسين بالذكاء الاصطناعي</span>
                                </label>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="button-group">
                            <button
                                className="preview-button"
                                onClick={handlePreviewSelection}
                                disabled={profileLoading || !profileJobDescription}
                            >
                                {profileLoading && !showPreview ? (
                                    <>
                                        <span className="spinner"></span>
                                        <span>جاري تحميل المعاينة...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                            <path d="M8 3a5 5 0 00-5 5c0 1.5.7 2.8 1.8 3.7L3 13.5l.7.7 1.8-1.8A5 5 0 1013 8a5 5 0 00-5-5zm0 1a4 4 0 110 8 4 4 0 010-8z"/>
                                        </svg>
                                        <span>معاينة الاختيار</span>
                                    </>
                                )}
                            </button>

                            <button
                                className="generate-button"
                                onClick={handleGenerateTailored}
                                disabled={profileLoading || !profileJobDescription}
                            >
                                {profileLoading && showPreview ? (
                                    <>
                                        <span className="spinner"></span>
                                        <span>جاري إنشاء السيرة الذاتية...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm1 10.5L5.5 8l.707-.707L8 9.086l3.793-3.793.707.707L9 11.5z"/>
                                        </svg>
                                        <span>إنشاء سيرة ذاتية مخصصة</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {profileError && (
                            <div className="error-message">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M8 1a7 7 0 100 14A7 7 0 008 1zM7 5h2v4H7V5zm0 5h2v2H7v-2z"/>
                                </svg>
                                <span>{profileError}</span>
                            </div>
                        )}

                        {/* Selection Preview */}
                        {showPreview && selectionPreview && (
                            <div className="selection-preview">
                                <h3 className="preview-title">معاينة اختيار المحتوى</h3>
                                <div className="preview-content">
                                    {selectionPreview.experience && selectionPreview.experience.length > 0 && (
                                        <div className="preview-section">
                                            <h4>الخبرة ({selectionPreview.experience.length} عناصر)</h4>
                                            <ul>
                                                {selectionPreview.experience.map((exp, idx) => (
                                                    <li key={idx}>
                                                        {exp.title} - {exp.company}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {selectionPreview.projects && selectionPreview.projects.length > 0 && (
                                        <div className="preview-section">
                                            <h4>المشاريع ({selectionPreview.projects.length} عناصر)</h4>
                                            <ul>
                                                {selectionPreview.projects.map((proj, idx) => (
                                                    <li key={idx}>{proj.name}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {selectionPreview.skills && selectionPreview.skills.length > 0 && (
                                        <div className="preview-section">
                                            <h4>المهارات ({selectionPreview.skills.length} عناصر)</h4>
                                            <ul>
                                                {selectionPreview.skills.map((skill, idx) => (
                                                    <li key={idx}>{skill.name}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {selectionPreview.certifications && selectionPreview.certifications.length > 0 && (
                                        <div className="preview-section">
                                            <h4>الشهادات ({selectionPreview.certifications.length} عناصر)</h4>
                                            <ul>
                                                {selectionPreview.certifications.map((cert, idx) => (
                                                    <li key={idx}>{cert.name}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Manual Mode Result Section */}
                {activeTab === 'manual' && generatedResume && (
                    <div className="result-section">
                        <div className="result-header">
                            <h3 className="result-title">
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11H9V9h2v4zm0-5H9V6h2v2z"/>
                                </svg>
                                السيرة الذاتية المُنشأة
                            </h3>
                            <button
                                onClick={handleDownloadDocx}
                                className="download-button"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M8 10.5l-3.5-3.5.707-.707L7.5 8.586V2h1v6.586l2.293-2.293.707.707L8 10.5zM3 12h10v2H3v-2z"/>
                                </svg>
                                <span>تحميل DOCX</span>
                            </button>
                        </div>
                        <div className="markdown-preview">
                            <pre className="markdown-content">{generatedResume}</pre>
                        </div>
                    </div>
                )}

                {/* Profile Mode Result Section */}
                {activeTab === 'profile' && tailoredResume && (
                    <div className="result-section">
                        <div className="result-header">
                            <div className="result-title-group">
                                <h3 className="result-title">
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                        <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm1 11H9V9h2v4zm0-5H9V6h2v2z"/>
                                    </svg>
                                    تم إنشاء السيرة الذاتية المخصصة
                                </h3>
                            </div>
                            <div className="download-buttons">
                                <button
                                    onClick={handleDownloadDocx}
                                    className="download-button"
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                        <path d="M8 10.5l-3.5-3.5.707-.707L7.5 8.586V2h1v6.586l2.293-2.293.707.707L8 10.5zM3 12h10v2H3v-2z"/>
                                    </svg>
                                    <span>DOCX</span>
                                </button>
                                <button
                                    onClick={handleDownloadPdf}
                                    className="download-button pdf"
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                        <path d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.707A1 1 0 0 0 13.707 4L10 .293A1 1 0 0 0 9.293 0H4zm5.5 1.5v2a1 1 0 0 0 1 1h2l-3-3z"/>
                                    </svg>
                                    <span>TXT</span>
                                </button>
                            </div>
                        </div>

                        {/* Match Analysis Section */}
                        <div className="match-analysis-section">
                            <div className="match-score-card">
                                <div className="score-circle" style={{
                                    '--score-percent': tailoredResume.matchScore || 0,
                                    background: `conic-gradient(
                                        ${tailoredResume.matchScore >= 70 ? '#22c55e' : tailoredResume.matchScore >= 50 ? '#eab308' : '#ef4444'}
                                        ${(tailoredResume.matchScore || 0) * 3.6}deg,
                                        #e5e7eb ${(tailoredResume.matchScore || 0) * 3.6}deg
                                    )`
                                }}>
                                    <div className="score-inner">
                                        <span className="score-number">{tailoredResume.matchScore || 0}%</span>
                                        <span className="score-text">تطابق</span>
                                    </div>
                                </div>
                                <div className="score-details">
                                    <h4>نسبة التطابق مع الوظيفة</h4>
                                    <p className="score-description">
                                        {tailoredResume.matchScore >= 70
                                            ? 'تطابق ممتاز! ملفك الشخصي يتوافق بشكل جيد مع هذا الدور.'
                                            : tailoredResume.matchScore >= 50
                                            ? 'تطابق جيد مع مجال للتحسين.'
                                            : 'فكر في إبراز المزيد من الخبرات ذات الصلة.'}
                                    </p>
                                </div>
                            </div>

                            {/* AI Analysis Details */}
                            {tailoredResume.aiMatchAnalysis && (
                                <div className="ai-analysis">
                                    {tailoredResume.aiMatchAnalysis.strengths?.length > 0 && (
                                        <div className="analysis-card strengths">
                                            <h5>
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                    <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm-.5 10.5l-3-3 .707-.707L7.5 10.086l4.293-4.293.707.707-5 5z"/>
                                                </svg>
                                                نقاط القوة الرئيسية
                                            </h5>
                                            <ul>
                                                {tailoredResume.aiMatchAnalysis.strengths.map((s, i) => (
                                                    <li key={i}>{s}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {tailoredResume.aiMatchAnalysis.matched_requirements?.length > 0 && (
                                        <div className="analysis-card matched">
                                            <h5>
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                    <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm.5 3v4.5H12v1H7.5V4h1z"/>
                                                </svg>
                                                المتطلبات المتوافقة
                                            </h5>
                                            <ul>
                                                {tailoredResume.aiMatchAnalysis.matched_requirements.slice(0, 5).map((r, i) => (
                                                    <li key={i}>{r}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {tailoredResume.aiMatchAnalysis.gaps?.length > 0 && (
                                        <div className="analysis-card gaps">
                                            <h5>
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                                    <path d="M8 1a7 7 0 100 14A7 7 0 008 1zM7 5h2v4H7V5zm0 5h2v2H7v-2z"/>
                                                </svg>
                                                مجالات للتطوير
                                            </h5>
                                            <ul>
                                                {tailoredResume.aiMatchAnalysis.gaps.slice(0, 3).map((g, i) => (
                                                    <li key={i}>{g}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Preview Tabs */}
                        <div className="preview-tabs">
                            <button
                                className={`preview-tab-btn ${previewTab === 'markdown' ? 'active' : ''}`}
                                onClick={() => setPreviewTab('markdown')}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M4 2h8a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2zm1 2v1h6V4H5zm0 3v1h6V7H5zm0 3v1h4v-1H5z"/>
                                </svg>
                                ماركداون
                            </button>
                        </div>

                        {/* Markdown Preview */}
                        {previewTab === 'markdown' && (
                            <div className="markdown-preview">
                                <pre className="markdown-content">{tailoredResume.markdown}</pre>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
